/**
 * Canonical AI-credits 402 detection — one normalizer for EVERY backend shape.
 *
 * The platform returns an "insufficient credits" 402 in several historical
 * shapes (audited): the orchestrator's shared handler
 * (`{error:"insufficient_ai_credits", required, available, feature, purchase_url}`),
 * its meter/reserve route (`{detail:{error:"insufficient_credits", ...}}`),
 * content-service (`{detail:{code:"insufficient_credits", ...}}`), and
 * b2b/memory/rag (`{detail:{message, required, available, feature}}` with no
 * marker at all). Every AI-credit consumer must react the same way, so this
 * module flattens all of them into one {@link NormalizedInsufficientCredits}.
 *
 * Pure (no imports from ./client) so it can be used from the API client itself
 * without an import cycle. The `ApiError`-aware guard lives in the banner.
 */

/** The unified, flattened insufficient-credits payload. */
export interface NormalizedInsufficientCredits {
  feature?: string;
  required?: number;
  available?: number;
  purchaseUrl?: string;
  /** Human-facing message (French), if the backend supplied one. */
  detail?: string;
  /**
   * When the blocked call was persisted as a resumable run (L-resume), its id.
   * The CreditGuardHost stores this so it can offer "Recharger et continuer"
   * (POST /api/v1/runs/{id}/retry) after the top-up.
   */
  blockedRunId?: string;
}

/** Window event dispatched on any insufficient-credits 402 (CreditGuardHost listens). */
export const AI_CREDITS_INSUFFICIENT_EVENT = 'psn:ai-credits-insufficient';

const INSUFFICIENT_MARKERS = new Set(['insufficient_ai_credits', 'insufficient_credits']);

function asNum(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}
function asStr(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

/**
 * Detect + flatten an insufficient-credits 402 from ANY service shape. Returns
 * the normalized payload, or `null` when (status, body) is not a credit
 * shortfall. Safe to call with anything.
 */
export function parseInsufficientCredits(
  status: number,
  body: unknown,
): NormalizedInsufficientCredits | null {
  if (status !== 402 || !body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  const detailObj =
    b.detail && typeof b.detail === 'object' ? (b.detail as Record<string, unknown>) : null;

  const marker =
    asStr(b.error) ??
    asStr(b.code) ??
    (detailObj ? asStr(detailObj.error) ?? asStr(detailObj.code) : undefined);

  const required = asNum(b.required) ?? (detailObj ? asNum(detailObj.required) : undefined);
  const available = asNum(b.available) ?? (detailObj ? asNum(detailObj.available) : undefined);

  // A credit shortfall is either explicitly marked, OR a 402 that carries both
  // `required` and `available` numbers (HTTP 402 is used ONLY for credits on
  // this platform — see the audit), which covers the marker-less b2b/rag shape.
  const isShortfall =
    (marker != null && INSUFFICIENT_MARKERS.has(marker)) ||
    (required != null && available != null);
  if (!isShortfall) return null;

  const detail =
    asStr(b.detail) ?? // `detail` is a plain string (FastAPI HTTPException(detail="…"))
    (detailObj ? asStr(detailObj.message) ?? asStr(detailObj.detail) : undefined) ??
    asStr(b.message);
  const feature = asStr(b.feature) ?? (detailObj ? asStr(detailObj.feature) : undefined);
  const purchaseUrl =
    asStr(b.purchase_url) ?? (detailObj ? asStr(detailObj.purchase_url) : undefined);
  const blockedRunId =
    asStr(b.blocked_run_id) ?? (detailObj ? asStr(detailObj.blocked_run_id) : undefined);

  return { feature, required, available, purchaseUrl, detail, blockedRunId };
}

/**
 * localStorage key holding the id of the last credit-blocked run (L-resume).
 * Survives the Stripe redirect (full page reload) so the settings page can
 * offer "Recharger et continuer" for that exact run after the top-up.
 */
export const BLOCKED_RUN_STORAGE_KEY = 'psn:blocked-run-id';

/** Persist a blocked run id for the post-top-up resume flow (best-effort). */
export function rememberBlockedRun(runId: string | undefined | null): void {
  if (typeof window === 'undefined' || !runId) return;
  try {
    window.localStorage.setItem(BLOCKED_RUN_STORAGE_KEY, runId);
  } catch {
    /* storage unavailable — resume simply won't be offered */
  }
}

/** Read (and clear) the stored blocked run id, if any. */
export function takeBlockedRun(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const id = window.localStorage.getItem(BLOCKED_RUN_STORAGE_KEY);
    if (id) window.localStorage.removeItem(BLOCKED_RUN_STORAGE_KEY);
    return id;
  } catch {
    return null;
  }
}

/** Dispatch the global insufficient-credits event (no-op outside a browser). */
export function emitInsufficientCredits(payload: NormalizedInsufficientCredits): void {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  try {
    window.dispatchEvent(new CustomEvent(AI_CREDITS_INSUFFICIENT_EVENT, { detail: payload }));
  } catch {
    /* CustomEvent unavailable — the thrown ApiError still surfaces at the call site */
  }
}
